import { MsgType } from 'matrix-js-sdk'

export interface Mention {
  id: string
  display: string
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// Turn a plain composer string + the mentions the user picked into HTML with
// matrix.to pills, plus the list of mentioned user ids. Only mentions whose
// "@Display" token is still present in the text are emitted.
export function renderMentions(text: string, mentions: Mention[]): { html: string; ids: string[] } {
  let html = escapeHtml(text)
  const ids: string[] = []
  for (const m of mentions) {
    const token = `@${m.display}`
    if (!text.includes(token)) continue
    ids.push(m.id)
    const pill = `<a href="https://matrix.to/#/${encodeURIComponent(m.id)}">${escapeHtml(m.display)}</a>`
    html = html.split(escapeHtml(token)).join(pill)
  }
  html = html.replace(/\n/g, '<br>')
  return { html, ids: [...new Set(ids)] }
}

// Build the text-message content, adding HTML + m.mentions only when the message
// actually references someone. Extra relation fields (reply/thread) are merged by
// the caller.
export function buildTextContent(text: string, mentions: Mention[]): Record<string, unknown> {
  const { html, ids } = renderMentions(text, mentions)
  const content: Record<string, unknown> = { msgtype: MsgType.Text, body: text }
  if (ids.length > 0) {
    content.format = 'org.matrix.custom.html'
    content.formatted_body = html
    content['m.mentions'] = { user_ids: ids }
  }
  return content
}

// Detect an in-progress "@query" at the caret. The "@" must start the string or
// follow whitespace; the query runs to the caret and may contain spaces (to match
// multi-word display names), but not newlines.
export function getMentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret)
  const at = upto.lastIndexOf('@')
  if (at === -1) return null
  if (at > 0 && !/\s/.test(text[at - 1]!)) return null
  const query = upto.slice(at + 1)
  if (query.includes('\n') || query.length > 40) return null
  return { start: at, query }
}
